import { CheckCircle2, Circle, XCircle, AlertCircle } from "lucide-react";
import { STATUS_LABEL } from "./util";

/* ProgressStepper — visual rail of the workflow stages a case can travel
   through. Highlights the current stage, marks earlier stages complete,
   and dims future stages. Mirrors the 10-step operational arc PHS uses
   in production:
   New → Assigned → Awaiting Quote → Quote Review → Approved →
   Scheduled → In Progress → Completed → Paid → Closed.
*/

const STEPS = [
  { key: "NEW",              label: "New" },
  { key: "ASSIGNED",         label: "Assigned" },
  { key: "AWAITING_QUOTE",   label: "Awaiting Quote" },
  { key: "QUOTE_REVIEW",     label: "Quote Review", aliases: ["REVIEWING"] },
  { key: "APPROVED",         label: "Approved" },
  { key: "SCHEDULED",        label: "Scheduled" },
  { key: "IN_PROGRESS",      label: "In Progress" },
  { key: "COMPLETED",        label: "Completed" },
  { key: "PAID",             label: "Paid" },
  { key: "CLOSED",           label: "Closed" },
];

function indexOfStatus(status) {
  for (let i = 0; i < STEPS.length; i++) {
    if (STEPS[i].key === status) return i;
    if ((STEPS[i].aliases || []).includes(status)) return i;
  }
  return -1;
}

export default function ProgressStepper({ status, cancelled }) {
  const current = (status || "NEW").toUpperCase();
  const idx = indexOfStatus(current);

  if (cancelled || current === "CANCELLED") {
    return (
      <div className="bg-white border border-[#B91C1C]/30 rounded-xl p-5" data-testid="ws-progress-stepper">
        <div className="flex items-center gap-3">
          <XCircle size={18} className="text-[#B91C1C]"/>
          <div>
            <p className="text-[10.5px] tracking-[0.22em] uppercase text-[#B88A2A] font-semibold">Job Lifecycle</p>
            <p className="font-display text-lg text-[#071A33] mt-0.5">This job was cancelled.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E2DCD0] rounded-xl p-5 shadow-[0_1px_0_rgba(7,26,51,0.04)]" data-testid="ws-progress-stepper">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10.5px] tracking-[0.22em] uppercase text-[#B88A2A] font-semibold">Job Lifecycle</p>
          <p className="font-display text-lg text-[#071A33] mt-0.5">
            {idx >= 0
              ? <>Stage <span className="tabular-nums">{idx + 1}</span> of <span className="tabular-nums">{STEPS.length}</span> · {STATUS_LABEL[current] || current}</>
              : <>Status: {STATUS_LABEL[current] || current}</>}
          </p>
        </div>
      </div>

      {/* Track */}
      <div className="relative pt-1">
        {/* Bar */}
        <div className="absolute left-0 right-0 top-[18px] h-[3px] bg-[#E2DCD0] rounded-full" aria-hidden/>
        <div
          className="absolute left-0 top-[18px] h-[3px] bg-[#064E2E] rounded-full transition-all duration-500"
          style={{ width: idx < 0 ? "0%" : `${(idx / (STEPS.length - 1)) * 100}%` }}
          aria-hidden
        />

        {/* Nodes */}
        <ol className="relative grid grid-cols-5 lg:grid-cols-10 gap-1" data-testid="ws-stepper-nodes">
          {STEPS.map((s, i) => {
            const done = idx >= 0 && i < idx;
            const active = i === idx;
            const future = idx >= 0 && i > idx;
            const cls = active
              ? "bg-[#064E2E] text-white ring-4 ring-[#064E2E]/15 border-[#064E2E]"
              : done
                ? "bg-[#064E2E] text-white border-[#064E2E]"
                : "bg-white text-[#94A3B8] border-[#E2DCD0]";
            return (
              <li key={s.key} className="flex flex-col items-center text-center" data-testid={`ws-step-${s.key}`}>
                <span className={`w-[36px] h-[36px] rounded-full border-2 flex items-center justify-center transition relative z-10 ${cls}`}>
                  {done ? <CheckCircle2 size={16}/> : active ? <span className="text-[11px] font-semibold tabular-nums">{i + 1}</span> : <Circle size={12}/>}
                </span>
                <span className={`mt-2 text-[10.5px] leading-tight tracking-tight font-medium max-w-[90px] ${active ? "text-[#071A33]" : done ? "text-[#475569]" : "text-[#94A3B8]"}`}>
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
